import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { getCurrentUser, loginBySms, logout, sendSmsCode } from '@/api/auth'
import { refreshAuthToken } from '@/api/client'
import type { AuthTokens, CurrentUser } from '@/types/auth'
import { clearAuthTokens, readAuthTokens, saveAuthTokens } from '@/utils/auth-storage'

let authListenersRegistered = false
let restorePromise: Promise<void> | null = null

export const useAuthStore = defineStore('auth', () => {
  const tokens = ref<AuthTokens | null>(readAuthTokens())
  const user = ref<CurrentUser | null>(null)
  const initialized = ref(false)
  const loading = ref(false)
  let refreshTimer: number | undefined

  const isAuthenticated = computed(() => Boolean(tokens.value?.accessToken))

  function syncTokens(): void {
    tokens.value = readAuthTokens()
    scheduleTokenRefresh()
  }

  function scheduleTokenRefresh(): void {
    if (typeof window === 'undefined') return
    if (refreshTimer) window.clearTimeout(refreshTimer)
    if (!tokens.value) return

    const refreshInMs = Math.max((tokens.value.expiresIn - 300) * 1000, 1000)
    refreshTimer = window.setTimeout(async () => {
      const refreshed = await refreshAuthToken()
      if (refreshed) {
        syncTokens()
      } else {
        clear()
      }
    }, refreshInMs)
  }

  async function restore(): Promise<void> {
    if (restorePromise) return restorePromise
    restorePromise = (async () => {
      syncTokens()
      if (!tokens.value) {
        initialized.value = true
        return
      }
      try {
        const response = await getCurrentUser()
        user.value = response.data
      } catch {
        clear()
      } finally {
        initialized.value = true
      }
    })()
    try {
      await restorePromise
    } finally {
      restorePromise = null
    }
  }

  async function requestSmsCode(phone: string): Promise<number> {
    const response = await sendSmsCode({ phone })
    return response.data.expireSeconds
  }

  async function login(phone: string, code: string): Promise<void> {
    loading.value = true
    try {
      const response = await loginBySms(phone, code)
      saveAuthTokens(response.data)
      syncTokens()
      const currentUser = await getCurrentUser()
      user.value = currentUser.data
    } finally {
      loading.value = false
    }
  }

  async function signOut(): Promise<void> {
    try {
      if (tokens.value) await logout()
    } finally {
      clear()
    }
  }

  /**
   * 重新拉取 `/app/auth/me` 同步 `user`（用于充值成功后更新顶部 chip
   * 的 `pointsBalance`）。无 token 时直接返回 null，
   * 不抛错。鉴权失败由 `client` 拦截器统一处理。
   */
  async function refresh(): Promise<CurrentUser | null> {
    if (!tokens.value) return null
    try {
      const response = await getCurrentUser()
      user.value = response.data
      return user.value
    } catch {
      return null
    }
  }

  function clear(): void {
    if (refreshTimer && typeof window !== 'undefined') window.clearTimeout(refreshTimer)
    refreshTimer = undefined
    user.value = null
    tokens.value = null
    clearAuthTokens()
  }

  if (typeof window !== 'undefined' && !authListenersRegistered) {
    window.addEventListener('xuntu:tokens-updated', syncTokens)
    window.addEventListener('xuntu:auth-expired', clear)
    authListenersRegistered = true
  }

  return {
    tokens,
    user,
    initialized,
    loading,
    isAuthenticated,
    restore,
    requestSmsCode,
    login,
    signOut,
    refresh,
    clear,
  }
})
